import React, { useEffect, useState } from "react";
import { api } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";

/**
 * Concerns for next year (build plan §10.1 #8; D2 #14): anyone signed in adds
 * one (supplies, process, facility, other); a System Admin closes it with a
 * note. The season's full report is #26 (Round M5).
 */

interface Concern {
  noteKey: string;
  category: string;
  text: string;
  status: "Open" | "Closed";
  createdUtc: string;
  authorName: string;
  authorEmail: string;
  closedUtc: string;
  closedByName: string;
  closeNote: string;
}
interface Data {
  concerns: Concern[];
  categories: string[];
  can: { add: boolean; close: boolean; export: boolean };
}

function CloseForm({ c, onDone }: { c: Concern; onDone: (d: Concern[]) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { problems, check } = useProblems([[`cc-${c.noteKey}`, !note.trim(), "Write a note on how it was closed."]]);
  if (!open) return <button type="button" className="btn-secondary min-h-11" onClick={() => setOpen(true)}>Close…</button>;
  async function close() {
    setError(null);
    if (!check()) return;
    const r = await api<{ concerns: Concern[] }>("POST", `/api/concerns/${encodeURIComponent(c.noteKey)}/close`, { note });
    if (!r.ok) return setError(r.data.error ?? "Not closed.");
    onDone(r.data.concerns);
  }
  return (
    <div className="mt-2 w-full">
      <label htmlFor={`cc-${c.noteKey}`} className="field-label">How it was closed</label>
      <input id={`cc-${c.noteKey}`} className="field-input" {...invalid(`cc-${c.noteKey}`, problems)} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. added to next year's supply order" />
      <FieldProblem id={`cc-${c.noteKey}`} problems={problems} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-1 flex gap-2">
        <button type="button" className="btn-primary min-h-11" onClick={() => void close()}>Close the concern</button>
        <button type="button" className="btn-secondary min-h-11" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export function Concerns(): React.ReactElement {
  const [d, setD] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState("");
  const [text, setText] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { problems, check, reset } = useProblems([
    ["cn-cat", !category, "Choose the category."],
    ["cn-text", !text.trim(), "Write the concern."],
  ]);
  useEffect(() => {
    void api<Data>("GET", "/api/concerns").then((r) => (r.ok ? setD(r.data) : setError(r.data.error ?? "Could not load.")));
  }, []);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!d) return <p className="text-sm text-slate-600">Loading…</p>;
  async function add() {
    setFlash(null);
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ concerns: Concern[] }>("POST", "/api/concerns", { category, text });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not added.");
    setD({ ...d!, concerns: r.data.concerns });
    setCategory("");
    setText("");
    reset();
    setFlash("Concern added");
  }
  const open = d.concerns.filter((c) => c.status === "Open").length;
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/reports" className="text-program underline">← Reports</a></p>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">#26 Concerns for next year <span className="text-base font-normal text-slate-600">· {open} open · {d.concerns.length - open} closed</span></h2>
        {d.can.export && <a className="btn-secondary" href="/api/concerns/csv" download>Download CSV</a>}
      </div>
      {d.can.add && (
        <div className="section-card">
          <h3 className="mb-2 text-base font-semibold">Add a concern</h3>
          <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
            <div>
              <label htmlFor="cn-cat" className="field-label">Category</label>
              <select id="cn-cat" className="field-input min-h-11" {...invalid("cn-cat", problems)} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Choose…</option>
                {d.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <FieldProblem id="cn-cat" problems={problems} />
            </div>
            <div>
              <label htmlFor="cn-text" className="field-label">What should change next year</label>
              <textarea id="cn-text" className="field-input min-h-16" {...invalid("cn-text", problems)} value={text} onChange={(e) => setText(e.target.value)} />
              <FieldProblem id="cn-text" problems={problems} />
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void add()}>Add the concern</button>
            {flash && <span className="text-sm font-medium text-emerald-800" role="status">{flash}</span>}
          </div>
        </div>
      )}
      <div className="section-card">
        <h3 className="mb-2 text-base font-semibold">{open} open · {d.concerns.length - open} closed</h3>
        {d.concerns.length === 0 ? <p className="text-sm text-slate-600">None yet.</p> : (
          <ul className="divide-y divide-slate-100">
            {d.concerns.map((c) => (
              <li key={c.noteKey} className={`flex flex-wrap items-start justify-between gap-2 py-2 text-sm ${c.status === "Closed" ? "text-slate-500" : ""}`}>
                <span className="min-w-0">
                  <span className={`mr-1 rounded px-1.5 text-xs font-semibold ${c.status === "Open" ? "bg-amber-100 text-amber-950" : "bg-slate-200 text-slate-700"}`}>{c.status}</span>
                  <span className="mr-1 rounded bg-slate-100 px-1.5 text-xs">{c.category}</span>
                  <span className="whitespace-pre-wrap break-words">{c.text}</span>
                  <span className="block text-xs text-slate-500">{stamp(c.createdUtc)} · {c.authorName || c.authorEmail}{c.status === "Closed" ? ` · closed ${stamp(c.closedUtc)} by ${c.closedByName}: ${c.closeNote}` : ""}</span>
                </span>
                {d.can.close && c.status === "Open" && <CloseForm c={c} onDone={(concerns) => setD({ ...d, concerns })} />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
